import { FC, ReactNode, useMemo } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NextSeo } from "next-seo";
import { Toaster } from "react-hot-toast";
import { Toaster as SonnerToaster } from "shared/ui/shadcn/sonner";
import _merge from "lodash/merge";

import PrimaryNavigationV3 from "@/components/navigation/primary";
import CustomMeetingsToaster from "@/components/shared/custom-meetings-toaster";

import { seo } from "@/lib/seo";
import { AuthProvider } from "@/lib/auth";
import { RedirectsProvider } from "@/auth/redirects-provider";

import { GlobalContextProvider } from "@/context/global";
import { AppLayoutPagePropsI } from "@/lib/types/layout";
import { AppLayoutProvider, useAppLayoutContext } from "@/context/app-layout";
import { DialerGlobalProvider } from "@/context/dialer-global";
import { StorageProvider } from "@/context/local-storage";
import { ApiClientProvider } from "@/context/api-client";
import { WebsocketsProvider } from "@/context/websockets-context";

import { CookieMessageNotifier } from "@/helpers/cookies";
import { IntercomComponent } from "@/helpers/intercom";

import { WidgetsProvider } from "@/widgets/index";
import { ListTourProvider } from "@/modules/tours/list/context";

import { queryClient } from "@/api/query-client";

import { VersionControlModule } from "@/modules/version-control";
import { WhatsNewModule } from "@/modules/whats-new";
import { DailyTodosGate } from "@/modules/daily-todos/gate";
import { DocsSidebar } from "@/components/shared/docs/docs-sidebar";

interface AppLayoutPropsI {
  layoutProps: AppLayoutPagePropsI;
  children: ReactNode;
}

type ChildrenWithPageLayoutPropsI = AppLayoutPropsI;

const ChildrenWithPageLayoutProps: FC<ChildrenWithPageLayoutPropsI> = ({
  layoutProps,
  children,
}) => {
  const { appLayoutPageProps: userDefinedLayoutProps } = useAppLayoutContext();

  const {
    navTitle,
    isCurrentTimeVisible,
    isBackgroundVisible,
    contentContainerClassName,
    pageContainerClassName,
    headerClassName,
    docsPath,
  } = useMemo(
    () => _merge(layoutProps, userDefinedLayoutProps),
    [layoutProps, userDefinedLayoutProps]
  );

  return (
    <PrimaryNavigationV3
      title={navTitle}
      isCurrentTimeVisible={isCurrentTimeVisible}
      isBackgroundVisible={isBackgroundVisible}
      contentContainerClassName={contentContainerClassName}
      pageContainerClassName={pageContainerClassName}
      headerClassName={headerClassName}
      docsPath={docsPath}
    >
      {children}
    </PrimaryNavigationV3>
  );
};

const AppLayout: FC<AppLayoutPropsI> = ({ children, layoutProps }) => {
  const meta = layoutProps.meta || {};

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WebsocketsProvider>
          <GlobalContextProvider>
            <RedirectsProvider>
              <StorageProvider>
                <ApiClientProvider>
                  <NextSeo {...seo(meta)} />

                  <IntercomComponent />

                  <DialerGlobalProvider>
                    <ListTourProvider>
                      <WidgetsProvider>
                        <main data-theme="glencoco">
                          <Toaster
                            containerStyle={{
                              top: 80,
                            }}
                            position="top-center"
                            toastOptions={{
                              style: {
                                minWidth: "360px",
                              },
                            }}
                          />

                          {/* sonner — used by `shared/ui/shadcn/sonner` toasts
                              (e.g. scheduling-link instructions copy-feedback).
                              Without this Toaster mounted, those toast.success()
                              calls render nothing. */}
                          <SonnerToaster position="top-center" />

                          <CustomMeetingsToaster />

                          <VersionControlModule />

                          {/* C-16 S0: the login "What's new" announcements. */}
                          <WhatsNewModule />

                          {/* C-16 S6: the daily To-Dos reminder and snooze. */}
                          <DailyTodosGate />

                          <DocsSidebar />

                          <AppLayoutProvider>
                            <ChildrenWithPageLayoutProps
                              layoutProps={layoutProps}
                            >
                              {children}
                            </ChildrenWithPageLayoutProps>
                          </AppLayoutProvider>

                          <CookieMessageNotifier />
                        </main>
                      </WidgetsProvider>
                    </ListTourProvider>
                  </DialerGlobalProvider>
                </ApiClientProvider>
              </StorageProvider>
            </RedirectsProvider>
          </GlobalContextProvider>
        </WebsocketsProvider>
      </AuthProvider>

      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default AppLayout;
